import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DataRole } from '@mode-switch/express';
import { Model } from '@mode-switch/model';
import { Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { ChartActions, fromChartSelectors, peekRedoStack, peekUndoStack, TempActions } from '@mode/charts/domain';
import {
  ChartDrillDown,
  ChartFormatHelpers,
  EncodingHelpers,
  Flamingo,
  QueryBuilder,
  SwitchSchemaModel,
} from '@mode/charts/util-vegas';
import { LegacyReportFilterControl } from '@mode/filters/util';
import { ofType } from '@mode/legacy/util-domain';
import { fromReportFilterSelectors, reportFilterActions } from '@mode/report-viewer/domain';
import { ViewDataModalFacade, VirtualScrollFacade } from '@mode/reports/domain';
import { ChartTypes, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { isDesignerMode, DEFAULT_FLAMINGO_PAGE_SIZE, isPresent } from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { CsvExportEvents, recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade, FlamingoActions, SwitchAttrsActions } from '@mode/switch-views/contract';
import { UIRouterGlobals } from '@uirouter/core';
import { isEqual } from 'lodash';
import { combineLatest, merge, Observable, Subscription } from 'rxjs';
import {
  combineLatestWith,
  debounceTime,
  filter,
  finalize,
  first,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Actions } from '~/actions';
import { SharedChartCommandActionType } from '~/actions/chart-commands/shared.actions';
import { ReportLayoutActions } from '~/actions/report-layout.actions';
import { FLAMINGO_UNSUPPORTED_CHARTS } from '~/common/constants';
import { addReportFiltersToChartEncoding } from '~/lib/chart-encoding-helpers';
import { isChart } from '~/lib/chart-helpers';
import { getSnapshotFromAction } from '~/lib/charts/chart-pipes';
import { ChartViewData } from '~/lib/charts/chart-view-data';
import { ChartSelectors } from '~/selectors/chart.selectors';
import { AppStore } from '~/types/main.types';
import { ChartViewActionType } from '../actions/chart-commands/view.actions';
import { reportFilterControlsSelector } from '../reducers/selectors';

export class ChartViewService {
  public static $inject = [
    '$window',
    '$uiRouterGlobals',
    'RxStoreService',
    'actions$',
    'DataViewFacade',
    'ViewDataModalFacade',
    'VirtualScrollFacade',
    'ChartFacade',
    'FeatureFlagsFacade',
  ];

  constructor(
    private $window,
    private $uiRouterGlobals: UIRouterGlobals,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private dataViewFacade: DataViewFacade,
    private viewDataModalFacade: ViewDataModalFacade,
    private virtualScrollFacade: VirtualScrollFacade,
    private chartFacade: ChartFacade,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    // TODO: Consolidate this with the chart-data-view implementation
    this.actions$
      .pipe(
        getSnapshotFromAction(this.store, ChartViewActionType.UpdateEncoding),
        map(({ action, token, snapshot }) =>
          ChartActions.updateChartTemp({
            token,
            chart: {
              ...snapshot,
              encoding: action.result.encoding,
            },
            description: 'update encoding',
          })
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    // Change hierarchy
    // Effect for GF flow
    this.actions$
      .pipe(
        getSnapshotFromAction(this.store, ChartViewActionType.ChangeHierarchy),
        switchMap(({ action, token, snapshot }) =>
          this.store
            .select(fromChartSelectors.createQueryTokenSelector(token))
            .pipe(map((queryToken) => ({ action, token, snapshot, queryToken })))
        ),
        withLatestFrom(this.dataViewFacade.model$),
        filter(([{ action, snapshot, queryToken }, model]) => {
          const canChange = EncodingHelpers.canChangeHierarchy(snapshot.encoding, model, queryToken);
          return action.forward ? canChange.forward : canChange.back;
        }),
        map(([{ action, snapshot, token, queryToken }, model]) => {
          const encoding = EncodingHelpers.changeHierarchy(
            snapshot.encoding,
            Switch.Views.find(model, queryToken),
            action.forward
          ).encoding;
          return ChartActions.updateChartTemp({
            token,
            description: 'change hierarchy',
            chart: {
              ...snapshot,
              encoding,
            },
          });
        })
      )
      .subscribe((action) => this.store.dispatch(action));

    // Drill Down
    this.actions$
      .pipe(
        getSnapshotFromAction(this.store, ChartViewActionType.DrillDown),
        switchMap(({ action, token, snapshot }) =>
          this.store
            .select(fromChartSelectors.createQueryTokenSelector(token))
            .pipe(map((queryToken) => ({ action, token, snapshot, queryToken })))
        ),
        withLatestFrom(this.dataViewFacade.model$),
        map(([{ action, snapshot, token, queryToken }, model]) => {
          const schemaModel = SwitchSchemaModel.make(model, queryToken);
          const encoding = ChartDrillDown.encode(schemaModel, snapshot.encoding, action.target).encoding;
          const chartType = ChartDrillDown.newChartType(snapshot.chartType, action.target);
          return ChartActions.updateChartTemp({
            token,
            description: 'drilldown',
            chart: {
              ...snapshot,
              encoding,
              chartType,
            },
          });
        })
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        getSnapshotFromAction(this.store, ChartViewActionType.DynamicDrillDown),
        switchMap(({ action, token, snapshot }) =>
          this.store
            .select(fromChartSelectors.createQueryTokenSelector(token))
            .pipe(map((queryToken) => ({ action, token, snapshot, queryToken })))
        ),
        withLatestFrom(this.dataViewFacade.model$),
        map(([{ action, snapshot, token, queryToken }, model]) => {
          const modeView = Switch.Views.find(model, queryToken);
          if (!modeView) {
            throw new Error(`ModeView is invalid for queryToken: ${queryToken}`);
          }
          const encoding = ChartDrillDown.updateEncodingForDynamicDrillDown(
            model,
            modeView,
            snapshot.encoding,
            action.target,
            action.targetFieldId,
            action.drillToField,
            action.dateTruncUnit
          );

          return ChartActions.updateChartTemp({
            token,
            description: 'dynamic drill down',
            chart: {
              ...snapshot,
              encoding,
            },
          });
        })
      )
      .subscribe((action) => this.store.dispatch(action));

    // View Data in viz-designer
    this.actions$
      .pipe(
        ofType(SharedChartCommandActionType.ViewChartData),
        switchMap((action) =>
          combineLatest(
            [action],
            this.store.selectNoEmpty(fromChartSelectors.createQueryTokenSelector(action.chartToken)),
            this.store.selectNoEmpty(fromChartSelectors.createEncodingSelector(action.chartToken)),
            this.store.select(fromChartSelectors.createChartTitleSelector(action.chartToken)),
            this.dataViewFacade.model$
          ).pipe(first())
        ),
        tap(([action, queryToken, encoding, title, model]) =>
          this.viewDataModalFacade.openModal(
            ChartViewData.encodingFromTarget(model, queryToken, encoding, action.target),
            ChartViewData.encodingSeriesDetail(model, queryToken, encoding, action.target),
            queryToken,
            title
          )
        )
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(ChartActions.viewUnaggregatedChartData),
        switchMap((action) =>
          combineLatest(
            [action],
            this.store.selectNoEmpty(fromChartSelectors.createQueryTokenSelector(action.chartToken)),
            this.store.selectNoEmpty(fromChartSelectors.createEncodingSelector(action.chartToken)),
            this.store.select(fromChartSelectors.createChartTitleSelector(action.chartToken)),
            this.store.select(reportFilterControlsSelector),
            this.dataViewFacade.model$
          ).pipe(first())
        ),
        tap(([action, queryToken, encoding, title, filters, model]) => {
          recordEvent(CsvExportEvents.viewDataCsvExportClick({ token: action.chartToken }));
          const encWithFilters = this.addValidReportFiltersToEncoding(model, queryToken, encoding, filters);
          this.viewDataModalFacade.openModal(
            ChartViewData.encodingFromChart(model, queryToken, encWithFilters),
            ChartViewData.encodingSeriesDetail(model, queryToken, encoding),
            queryToken,
            (title || 'Untitled Chart') + ' (unaggregated)'
          );
        })
      )
      .subscribe();
  }

  dispatchCopyChart(token: string) {
    this.chartFacade.copyToVizStorage(token);
  }

  undo(token: string) {
    this.store.dispatchThunkPipe(
      fromChartSelectors.createHistorySelector(token),
      (obs) => obs.pipe(filter((history) => peekUndoStack(history) != null)),
      (history) => {
        if (history.undoStack.length > 0) {
          return ChartActions.undoTemp({
            token: token,
            snapshot: peekUndoStack(history),
          });
        }
      }
    );
  }

  redo(token: string) {
    this.store.dispatchThunkPipe(
      fromChartSelectors.createHistorySelector(token),
      (obs) => obs.pipe(filter((history) => peekRedoStack(history) != null)),
      (history) => {
        if (history.redoStack.length > 0) {
          return ChartActions.redoTemp({
            token: token,
            snapshot: peekRedoStack(history),
          });
        }
      }
    );
  }

  viewDataWithChart(target: ChartTypes.MarkTarget, title: string, chartToken: string, queryToken: string) {
    this.store.dispatch({
      type: TempActions.Type.ExploreChartWithData,
      target,
      chartToken,
    });
  }

  getWatchChartTokens$() {
    // Report chart/table queries
    const chartTokens$ = this.actions$.pipe(
      ofType(
        ChartActions.loadChartsSuccess,
        ReportLayoutActions.Type.AddViz,
        ReportLayoutActions.Type.RemoveViz,
        ChartActions.createChartSuccess,
        ChartActions.destroyChartSuccess,
        TempActions.Type.DestroyQuerySuccess,
        ChartActions.loadChartsFromSwitchViewSuccess,
        reportFilterActions.applyReportFilters
      ),
      // Kick
      startWith({}),
      // TODO: upgrade this flow to new state
      //
      // Since this effect is shared across the editor and viewer contexts, we'd need to hydrate
      // v2ChartTokensInLayout$ in both flows for it to work in both places. The commented out
      // line works in the Viewer context, but because the Editor still relies heavily on
      // SelectedReportState, it will be a bigger lift to use it there. We can revert the Viewer
      // flow to use legacy state because for the time being BOTH legacy and new states are being
      // loaded in the Viewer. This will not be true once we start deprecating legacy state, so
      // this should get updated as soon as we're able to model Editor state in the new pattern.
      //
      // withLatestFrom(isGlorious ? this.reportViewerFacade.v2ChartTokensInLayout$ : watched$, (_, tokens) => tokens)
      switchMap(() => {
        return this.store.select(ChartSelectors.getWatchedCharts);
      })
    );

    // Array of filter controls that have changed from prev state -> next state.
    // If a filter has been removed it will be included in the diffed set
    const filtersDiff$ = this.store.select(reportFilterControlsSelector).pipe(
      startWith([] as LegacyReportFilterControl[]),
      pairwise(),
      map(([prevFilters, nextFilters]) => {
        const diff: LegacyReportFilterControl[] = [];

        // Collect filters that have been removed
        prevFilters.forEach((prev) => {
          const next = nextFilters.find((f) => f.token === prev.token);
          if (next === undefined) {
            diff.push(prev);
          }
        });

        // Collect next filters that are either
        // 1. new. filter doesn't exist in the prev set
        // 2. existing -> changed. they exist in the prev set, but are different
        nextFilters.forEach((next) => {
          const prev = prevFilters.find((f) => f.token === next.token);
          // 1. new
          if (prev === undefined) {
            diff.push(next);
          }
          // 2. changed
          else if (!isEqual(prev, next)) {
            diff.push(next);
          }
        });

        return diff;
      }),
      shareReplay(1)
    );

    const chartSubs: Record<string, Subscription> = Object.create(null);
    return chartTokens$.pipe(
      finalize(() => {
        // Clear all
        for (const token of Object.keys(chartSubs)) {
          chartSubs[token]?.unsubscribe();
          delete chartSubs[token];
        }
      }),
      tap((tokens) => {
        for (const token of tokens) {
          // Add if not already present
          chartSubs[token] ??= this.createReportChartObservable(token, filtersDiff$).subscribe({
            error(err) {
              console.error(err);
            },
            next: (result) => {
              recordEvent('flamingo-query', 'generate-chart-plan', {
                traceId: result.plan.id,
                refId: result.chartToken,
                plan: result.plan,
                queryRunToken: result.runToken,
                vizToken: token,
              });
              this.store.dispatch(
                FlamingoActions.executePlan({
                  plan: result.plan,
                  refId: result.chartToken,
                  queryRunToken: result.runToken,
                  allowRefresh: true,
                  vizToken: token,
                })
              );
            },
          });
        }
        // Clear removed
        for (const token of Object.keys(chartSubs)) {
          if (!tokens.includes(token)) {
            chartSubs[token]?.unsubscribe();
            delete chartSubs[token];
          }
        }
      })
    );
  }

  /**
   * Create the chart$ that gets used in the plans observable.
   */
  private createChart(chartToken: string, filtersDiff$: Observable<LegacyReportFilterControl[]>) {
    return this.store.selectNoEmpty(fromChartSelectors.createChartTypeEncodingSelector(chartToken)).pipe(
      filter(({ encoding, chartType }) => EncodingHelpers.isChartEncodingComplete(encoding, chartType)),
      switchMap(({ queryToken, chartType, encoding }) => {
        let runReadyByQueryToken = this.dataViewFacade.createRunReadyByQueryToken(queryToken);
        if (isDesignerMode(this.$window.document.body)) {
          runReadyByQueryToken = this.dataViewFacade.createRunReadyOrRunningByQueryToken(queryToken);
        }
        return runReadyByQueryToken.pipe(
          filter((ready) => ready && isPresent(encoding)),
          switchMap(() =>
            combineLatest([
              this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(queryToken),
              this.virtualScrollFacade.getInViewportObservable(chartToken).pipe(
                debounceTime(25), // to avoid fetching all viz data when scrolling very quickly
                filter((inViewport) => inViewport),
                first()
              ),
            ]).pipe(map(([runToken]) => runToken))
          ),
          switchMap((runToken) =>
            combineLatest([
              this.actions$.pipe(
                // respond to create filter action so it gets options filled in
                ofType(
                  reportFilterActions.applyReportFilters,
                  reportFilterActions.updateReportFilterDefaultsSuccess,
                  reportFilterActions.destroyReportFilterSuccess
                ),
                withLatestFrom(this.dataViewFacade.model$, filtersDiff$),
                filter(([, model, filters]) => {
                  return (
                    // Just clicking Apply.. :/
                    filters.length === 0 ||
                    // Or, some filter applicable to this chart was changed
                    filters.some((control) => {
                      if (control !== undefined) {
                        return Flamingo.getValidReportFilters(model, queryToken, [control]).length > 0;
                      }
                    })
                  );
                }),
                startWith({})
              ),
              this.actions$.pipe(
                // On update calculation, rerun this
                ofType(SwitchAttrsActions.updateSwitchViewAttrSuccess),
                filter((a) => a.queryToken === queryToken),
                startWith({})
              ),
            ]).pipe(
              map(
                // Drop action out of stream
                () => runToken
              )
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.store.select(fromChartSelectors.createChartFormatSelector(chartToken))
          ),
          map(([runToken, model, chartFormat]) => ({
            queryToken,
            model,
            runToken,
            chartFormat,
            chartToken,
            chartType,
            encoding,
          }))
        );
      })
    );
  }

  // Builds an observable for the given chart token that watches for any changes and
  // returns an ExecutePlan action for later dispatch.
  private createReportChartObservable(chartToken: string, filtersDiff$: Observable<LegacyReportFilterControl[]>) {
    const chart$ = this.createChart(chartToken, filtersDiff$);
    const adapter = new FlamingoAdapter();

    // Merge both chart/table observables together. Can't conflict since each one has an appropriate filter
    return merge(
      chart$.pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            chartType === 'table' &&
            EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
            EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length ===
              0
        ),
        switchMap((data) =>
          this.store.select(fromReportFilterSelectors.selectIsFiltersComplete).pipe(
            filter((complete) => complete),
            first(), // don't want this to keep firing after the first completion
            map(() => data) // don't need the result of the complete selector
          )
        ),
        withLatestFrom(this.store.select(reportFilterControlsSelector), (data, filters) => ({ ...data, filters })),
        tap(() => {
          this.store.dispatch(ChartActions.resetOffset({ token: chartToken }));
        }),
        map(({ runToken, queryToken, encoding, model, filters, chartFormat }) => {
          const filteredEncoding = this.addValidReportFiltersToEncoding(model, queryToken, encoding, filters);
          const pageSize = chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE;
          const plan = QueryBuilder.buildRecordsPlan(adapter, model, queryToken, filteredEncoding, pageSize, 0);
          return {
            runToken,
            chartToken,
            plan,
          };
        })
      ),
      chart$.pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            chartType !== 'table' &&
            FLAMINGO_UNSUPPORTED_CHARTS.indexOf(chartType) === -1 &&
            EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
            EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length ===
              0
        ),
        switchMap((data) =>
          this.store.select(fromReportFilterSelectors.selectIsFiltersComplete).pipe(
            filter((complete) => complete),
            first(), // don't want this to keep firing after the first completion
            map(() => data) // don't need the result of the complete selector
          )
        ),
        withLatestFrom(this.store.select(reportFilterControlsSelector), (data, filters) => ({ ...data, filters })),
        map(({ runToken, queryToken, model, filters, chartFormat, chartType, encoding }) => {
          const filteredEncoding = this.addValidReportFiltersToEncoding(model, queryToken, encoding, filters);
          const needsColorBackfill = isChart(chartType) && chartFormat.colors.map == null;
          const includeSortDomain = ChartFormatHelpers.useMuzeRenderer(chartType, chartFormat);
          return {
            runToken,
            chartToken,
            plan: QueryBuilder.buildPivotQuery(
              adapter,
              model,
              queryToken,
              filteredEncoding,
              needsColorBackfill,
              includeSortDomain
            ),
          };
        })
      )
    );
  }

  private addValidReportFiltersToEncoding(
    model: Model<DataRole>,
    queryToken: string,
    encoding: Encoding,
    filters: LegacyReportFilterControl[]
  ): Encoding {
    return addReportFiltersToChartEncoding(
      Model.Views.find(model, queryToken),
      encoding,
      Flamingo.getValidReportFilters(model, queryToken, filters)
    );
  }
}
